import { Button } from '@platform/ui-kit'
import Image from 'next/image'
import classnames from 'classnames'
import { m, LazyMotion, domAnimation, useAnimation, Variants } from 'framer-motion'
import { useCallback } from 'react'

import ResultsImage from './results-image.png'
import styles from './styles.module.scss'
import CardChip from './card-chip'

const CONTENT_VARIANTS: Variants = {
  start: { y: '30%' },
  end: {
    y: '0%',
    transition: { type: 'spring', bounce: 0 },
  },
}

type StaticImageData = {
  src: string
  height: number
  width: number
  placeholder?: string
}

type CaseStudyCardProps = {
  companyName: string
  caseResume: string
  href?: string
  badgeList: string[]
  className?: string
  image?: string | StaticImageData
}

export const CaseStudyCard = ({
  companyName,
  caseResume,
  href,
  badgeList,
  className,
  image,
}: CaseStudyCardProps) => {
  const contentControl = useAnimation()
  const onViewportEnter = useCallback(async () => {
    contentControl.start('end')
  }, [contentControl])

  return (
    <LazyMotion features={domAnimation} strict>
      <m.article
        onViewportEnter={onViewportEnter}
        variants={CONTENT_VARIANTS}
        initial="start"
        viewport={{ once: true, amount: 0.01 }}
        animate={contentControl}
        className={classnames(styles['case-study-card'], className)}
      >
        <div className={styles['case-study-card-content']}>
          <h2 className={styles['title']}>{companyName}</h2>
          <div className={styles['card-chips-container']}>
            {badgeList.map(badge => (
              <CardChip key={badge} content={badge} />
            ))}
          </div>
          <p className={styles['description']}>{caseResume}</p>
          {href && (
            <Button
              variant="primary"
              size="large"
              href={href}
              className={styles['read-more-button']}
              prefetch={false}
            >
              Read more
            </Button>
          )}
        </div>
        <div className={styles['image-wrapper']}>
          <Image
            src={image || ResultsImage}
            alt="Case studies card"
            fill
            sizes="(max-width: 1279px) 100vw, 69.9rem"
            className={styles['image']}
          />
        </div>
      </m.article>
    </LazyMotion>
  )
}

export default CaseStudyCard
